import { useState } from "react";
import type { Item } from "react-loqate";
import { splitAtMatch } from "./suggestion-highlight";

type AddressSuggestionProps = Omit<React.ComponentProps<"li">, "value"> & {
  suggestion: Item;
  /** What the donor has typed so far. */
  value?: string;
};

function moveFocusToSibling(event: React.KeyboardEvent<HTMLLIElement>) {
  const sibling =
    event.key === "ArrowDown"
      ? event.currentTarget.nextElementSibling
      : event.currentTarget.previousElementSibling;

  event.preventDefault();

  if (sibling instanceof HTMLElement) {
    sibling.focus();
  }
}

/**
 * Replaces react-loqate's `DefaultListItem`, whose children switch between
 * `[before, <strong>match</strong>, after]` and a single string as the donor
 * types. React then removes the old text nodes, and when something outside
 * React has already rewritten them — Chrome on iOS marking up addresses,
 * Google Translate — that removal throws `NotFoundError` and takes the address
 * search down (DONATION-PLATFORM-FRONTEND-2Y, -33).
 *
 * Every element here keeps one string child whatever the donor types, so React
 * only ever updates text in place. The library's own children (the same text,
 * unhighlighted) are dropped.
 *
 * The highlight can split a word across elements ("42 M" + "ain Street"), which
 * a screen reader may read as two words: the full text is the item's name.
 *
 * Focus moves onto the options themselves and Enter picks the focused one, so
 * selection follows focus, as in a single-select listbox.
 */
export function AddressSuggestion({
  suggestion,
  value = "",
  children: _unhighlightedText,
  onKeyDown,
  ...props
}: Readonly<AddressSuggestionProps>) {
  const [hasFocus, setHasFocus] = useState(false);
  const text = `${suggestion.Text} ${suggestion.Description}`;
  const { before, match, after } = splitAtMatch(text, value);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    onKeyDown?.(event);

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      moveFocusToSibling(event);
    }
  };

  return (
    <li // NOSONAR typescript:S6819: a native <option> cannot hold the <strong> highlight
      {...props}
      role="option"
      aria-label={text}
      aria-selected={hasFocus}
      onKeyDown={handleKeyDown}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
    >
      <span>{before}</span>
      <strong>{match}</strong>
      <span>{after}</span>
    </li>
  );
}
